import * as React from 'react';
import styles from './main-header.module.css';
import mixpanel from 'mixpanel-browser';
import clsx from 'clsx';
import { darkTheme } from 'theme';
import { Progress, Text, Tooltip } from 'ui';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { ReactComponent as NotificationsIcon } from 'assets/icons/24px/notifications.svg';
import { ReactComponent as CatapultIconLogo } from 'assets/catapult-icon-logo.svg';
import { useTranslations } from 'i18n';
import { NotificationsDrawer, useUnreadNotificationsCount } from 'notifications';
import { UpdateModal } from 'notifications/update-list-item';
import { OAuthDialog } from 'oauth';
import { Helmet } from 'react-helmet-async';
import { HashRouter, Link, Route, useHistory, useRouteMatch } from 'react-router-dom';
import { ReportSelector } from 'reporting/components/report-selector';
import { useSetRedirect } from 'reporting/hooks/use-set-redirect';
import { IconButton, Usetiful, ZenDesk, appTitle, countMax, useRunOnce } from 'shared';
import { useUnreadUpdatesCount } from 'updates';
import { AccountMenu } from 'user';
import { SupportButton } from 'support/button/support-button';
import { OnboardingAutoOpen } from 'support/onboarding';
import { OfflineBanner } from './offline-banner';
import { useGetFrontendConfig, useHasFeature } from 'api';
import { TfNotice, useGetNotices, useNoticesMutation } from 'api/notices';
import { FEATURE_FLAGS, useFeatureFlag } from 'feature-flags';
import { useShowPlanning } from 'planning/use-show-planning';
import { FrontendConfigFeaturesEnum, UserPermissionsEnum } from 'oas';
import { usePermission } from 'auth';
import { ActivityStatusContainer } from 'processing-status/activity-status-container';

interface MainHeaderProps {
    withNav?: boolean;
}

function getHrefToEditor() {
    const parts = window.location.pathname.split('/');
    const search = window.location.search;
    const singleSelectedActivity =
        parts.length > 3 && (parts[2] === 'activity' || parts[2] === 'match-day' || parts[2] === 'athlete-in-activity');
    const multiSelectedActivity = localStorage.latestSelected && (parts[2] === 'longitudinal' || parts[2] === 'weekly');

    if (singleSelectedActivity) {
        return '/editor/' + parts[3] + search;
    }

    if (multiSelectedActivity) {
        return '/editor/' + localStorage.latestSelected;
    }

    return '/editor';
}

export const MainHeader: React.FC<MainHeaderProps> = ({ withNav = true }) => {
    const isFetching = useIsFetching({
        // Don't show global loading indicator for polling requests (e.g. notifications)
        predicate: (query) =>
            query.state.fetchStatus === 'fetching' && !(query.options as { refetchInterval: boolean }).refetchInterval,
    });
    const isMutating = useIsMutating();
    const unreadUpdateCount = useUnreadUpdatesCount();
    const unreadNotificationCount = useUnreadNotificationsCount();
    const { __ } = useTranslations();
    const history = useHistory();
    const match = useRouteMatch();
    const setRedirect = useSetRedirect();

    const showPlanning = useShowPlanning();
    const showEditor = useHasFeature(FrontendConfigFeaturesEnum.ShowEditor);
    const showTimeline = useHasFeature(FrontendConfigFeaturesEnum.ShowTimeline);
    const showUpdates = useHasFeature(FrontendConfigFeaturesEnum.ShowUpdates);
    // Updates are shown in the notifications drawer, so the bell counts both
    const unreadCount = unreadNotificationCount + (showUpdates ? unreadUpdateCount : 0);
    const showLeague = useHasFeature(FrontendConfigFeaturesEnum.ShowLeague);
    const showReporting = useHasFeature(FrontendConfigFeaturesEnum.ShowReporting);

    const { data: frontendConfig } = useGetFrontendConfig();
    const releaseChinaRegion = useFeatureFlag(FEATURE_FLAGS.RELEASE_CHINA_REGION, false);
    const isReleasedInRegion = frontendConfig?.app.aws_default_region !== 'cn-north-1' || releaseChinaRegion;

    // only show settings if timeline or reporting is present
    const showSettings = usePermission(UserPermissionsEnum.Settings);
    const isSharedLinkLogin = usePermission(UserPermissionsEnum.SharedLink);

    const [isNotificationsDialogOpen, setIsNotificationsDialogOpen] = React.useState(false);
    const { data: notices, isFetchedAfterMount } = useGetNotices();
    const { mutate: updateNotices } = useNoticesMutation();
    const [loginNotice, setLoginNotice] = React.useState<TfNotice>();

    useRunOnce(() => {
        const notice = showUpdates && notices?.find((item) => item.notify_on_login === 1 && item.notified === 0);
        if (notice) {
            setLoginNotice(notice);
            updateNotices({ notified: true });
            mixpanel.track('NotificationModal', { modal: 'open', notification_id: notice.id });
        }
    }, isFetchedAfterMount && !!frontendConfig);

    function closeLoginNotice() {
        mixpanel.track('NotificationModal', { modal: 'close', notification_id: loginNotice?.id });
        setLoginNotice(undefined);
    }

    function toggleNotifications() {
        const isOpen = !isNotificationsDialogOpen;
        setIsNotificationsDialogOpen(isOpen);

        mixpanel.track(isOpen ? 'BellNotificationPanelOpened' : 'BellNotificationPanelClosed');
    }

    const handleCloseDialog = React.useCallback(() => {
        // history.push does not work by itself because of mixture of BrowserRouter and HashRouter
        window.location.href = '#';

        // replace so we don't need to click back twice to re-open
        history.replace({ hash: '' });
    }, [history]);

    const items = [
        {
            href: '/reporting',
            labelText: __('reporting.reporting'),
            show: showReporting && isReleasedInRegion,
            id: 'header-reporting',
        },
        {
            href: '/planning',
            labelText: __('header.planning'),
            show: showPlanning && isReleasedInRegion,
            id: 'header-planning',
        },
        {
            href: '/editor',
            labelText: __('header.editor'),
            show: showEditor && isReleasedInRegion,
            id: 'header-activity-editor',
        },
        {
            href: '/timeline',
            labelText: __('header.timeline'),
            show: showTimeline,
            id: 'header-timeline',
        },
        {
            href: '/settings',
            labelText: __('header.settings'),
            show: showSettings,
            id: 'header-settings',
        },
        {
            href: '/league',
            labelText: __('league_migration.league'),
            show: showLeague,
            id: 'header-league',
        },
    ];

    if (isSharedLinkLogin) {
        return (
            <MuiThemeProvider theme={darkTheme}>
                <div className={styles['layout-header']} data-testid="main-header">
                    <header className={styles['shared-nav-bar']}>
                        <div className={styles['shared-logo']}>
                            <CatapultIconLogo className={styles['logo-image']} />
                        </div>
                        <div className={styles['info-items']}>
                            <div className={styles['shared-menu']}>
                                <AccountMenu />
                            </div>
                        </div>
                    </header>
                </div>
            </MuiThemeProvider>
        );
    }

    return (
        <>
            <Helmet>
                <title>{appTitle()}</title>
            </Helmet>
            <a href="#main-content" className={styles['skip-link']}>
                {__('misc.skip_to_main')}
            </a>
            {isFetching || isMutating ? (
                <div className={styles['loader']} data-test="main-header-loader" style={{ zIndex: 99998 }}>
                    <Progress data-testid={`main-progress-bar`} />
                </div>
            ) : null}
            <div style={{ zIndex: 99999, position: 'relative' }}>
                <OfflineBanner />
            </div>
            <MuiThemeProvider theme={darkTheme}>
                <div className={styles['layout-header']} data-testid="main-header">
                    <header className={styles['nav-bar']}>
                        <a
                            href="/"
                            onClick={() => {
                                setRedirect();
                            }}
                            className={styles['logo-link']}
                        >
                            <CatapultIconLogo className={styles['logo-image']} />
                        </a>
                        <input className={styles['menu-btn']} type="checkbox" id="menu-btn" />
                        <label className={styles['menu-icon']} htmlFor="menu-btn">
                            <span className={styles['nav-icon']}></span>
                        </label>
                        {withNav && (
                            <>
                                <nav className={styles['menu']}>
                                    {items.map((item) => {
                                        const { show, href, labelText, id } = item;
                                        return (
                                            show && (
                                                <div
                                                    key={id}
                                                    id={id}
                                                    className={clsx(
                                                        styles['menu-item'],
                                                        href === '/reporting' && styles['menu-item-report-select'],
                                                        {
                                                            [styles['active']]:
                                                                (match?.path === '/' && href === '/') ||
                                                                (href !== '/' && match?.url.indexOf(href) === 0),
                                                        },
                                                    )}
                                                >
                                                    {href === '/reporting' ? (
                                                        <ReportSelector />
                                                    ) : (
                                                        <Link to={href === '/editor' ? getHrefToEditor() : href}>
                                                            <Text variant="body-large" className={styles['label']}>
                                                                {labelText}
                                                            </Text>
                                                        </Link>
                                                    )}
                                                </div>
                                            )
                                        );
                                    })}
                                </nav>
                                <div className={styles['info-items']}>
                                    <ActivityStatusContainer />
                                    <div
                                        className={clsx(styles['notifications-icon'], {
                                            [styles['unread']]: unreadCount,
                                        })}
                                    >
                                        <Tooltip content={__('notifications.notifications')}>
                                            <IconButton
                                                onClick={toggleNotifications}
                                                id="notifications-icon-button"
                                                aria-label={
                                                    __('notifications.notifications') +
                                                    (unreadCount ? ` (${unreadCount})` : '')
                                                }
                                                aria-expanded={isNotificationsDialogOpen}
                                            >
                                                <NotificationsIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <span
                                            className={styles['unread-badge']}
                                            id="notifications-unread-badge"
                                            aria-hidden
                                        >
                                            {countMax(unreadCount, 9)}
                                        </span>
                                    </div>
                                    <div
                                        className={styles['help-icon']}
                                        data-test="header-help-icon"
                                        data-testid="header-help-icon"
                                    >
                                        <SupportButton />
                                    </div>
                                    <div className={styles['account']}>
                                        <AccountMenu />
                                    </div>
                                </div>
                            </>
                        )}
                    </header>
                </div>
            </MuiThemeProvider>
            <HashRouter hashType="noslash">
                <Route path="/oauth">
                    <OAuthDialog open={true} onClose={handleCloseDialog} />
                </Route>
                <Route path="/notifications"></Route>
            </HashRouter>
            <NotificationsDrawer open={isNotificationsDialogOpen} onClose={toggleNotifications} />
            {loginNotice && <UpdateModal notice={loginNotice} open onClose={closeLoginNotice} />}
            <Usetiful />
            <ZenDesk />
            <OnboardingAutoOpen />
        </>
    );
};
